import { Children, ElementType, isValidElement, ReactElement, ReactNode } from "react";
import { SteppedFlowStep, SteppedFlowStepProps } from "./stepped-flow-step";
import { SteppedFlowFormikStep, SteppedFlowFormikStepProps } from "./stepped-flow-formik-step";
import type { IStepSchema } from "./stepped-flow.types";

/**
 * Structural check for `IStepSchema`, used to tell a genuine schema apart from a malformed
 * `validation` prop (e.g. `null` from an untyped caller, or a plain object missing `isValidSync`).
 *
 * Both members are checked, not just `isValidSync`: the shell calls `isValidSync` to gate a step and
 * `concat` to compose the whole-form schema, so an object carrying only the former would pass this
 * guard and then throw during composition — crashing instead of failing closed.
 * @param value Candidate `validation` prop value.
 * @returns Whether the value can be used as a step schema.
 */
export const isStepSchema = (value: unknown): value is IStepSchema =>
    typeof value === "object" &&
    value !== null &&
    "isValidSync" in value &&
    typeof value.isValidSync === "function" &&
    "concat" in value &&
    typeof value.concat === "function";

/**
 * Extract the step children so the header stepper and body can be derived from them.
 */
export const getSteps = (children: ReactNode): ReactElement<SteppedFlowStepProps>[] =>
    Children.toArray(children).filter(
        (child): child is ReactElement<SteppedFlowStepProps> => isValidElement(child) && child.type === SteppedFlowStep
    );

/**
 * Extract the Formik step children. Separate from `getSteps` because the Formik step requires a
 * `validation` prop that the base step has no use for.
 */
export const getFormikSteps = (children: ReactNode): ReactElement<SteppedFlowFormikStepProps>[] =>
    Children.toArray(children).filter(
        (child): child is ReactElement<SteppedFlowFormikStepProps> =>
            isValidElement(child) && child.type === SteppedFlowFormikStep
    );

/**
 * Extract the shell's footer child (if any) so it can be rendered beneath the body.
 * @param children   The shell's children.
 * @param footerType The shell's own footer component.
 */
export const getFooter = (children: ReactNode, footerType: ElementType): ReactNode =>
    Children.toArray(children).find((child) => isValidElement(child) && child.type === footerType);

/**
 * Clamp a (possibly out-of-range) active step index to a valid step. Returns 0 when there are no
 * steps, otherwise the nearest valid index in [0, stepsCount - 1]. This guards the body from
 * rendering `undefined` and the Mantine Stepper from receiving an invalid index.
 */
export const clampStepIndex = (active: number, stepsCount: number): number =>
    stepsCount === 0 ? 0 : Math.min(Math.max(active, 0), stepsCount - 1);
